/**
 * MarkdownRenderer — Obsidian-flavored markdown rendering via a unified/remark/rehype pipeline.
 *
 * Features:
 *   - Full GFM: tables, strikethrough, task lists, autolinks
 *   - Obsidian callouts: `> [!info] Title` → styled callout blocks
 *   - Wikilinks: `[[Note Title]]` → <a data-wikilink="Note Title" href="...">Note Title</a>
 *   - Code syntax highlighting via rehype-highlight (highlight.js)
 *   - Interactive task-list checkboxes (calls onToggleTask with task index)
 *
 * Props:
 *   content          — raw markdown string
 *   resolveWikilink  — optional fn(noteName: string) => string | null; returns URL if found, null if not (renders as .wikilink-new)
 *   onWikilinkClick  — optional fn(href: string) called when a resolved wikilink is clicked (prevents full-page navigation)
 *   onToggleTask     — optional fn(taskIndex: number) called when a checkbox is clicked
 *   className        — optional additional classes applied to the root wrapper
 */

import React, {
  Fragment,
  createElement,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import * as prod from 'react/jsx-runtime';
import { unified } from 'unified';
import type { Plugin } from 'unified';
import type { Root, Element } from 'hast';
import { visit } from 'unist-util-visit';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkRehype from 'remark-rehype';
import rehypeHighlight from 'rehype-highlight';
import rehypeReact from 'rehype-react';
import wikiLinkPlugin from 'remark-wiki-link';
import remarkCallouts from '@/lib/remarkCallouts';
import rehypeFoldableHeadings from '@/lib/rehypeFoldableHeadings';
import { cn } from '@/lib/utils';

// ─── rehypeEnableTaskCheckboxes ───────────────────────────────────────────────
// mdast-util-gfm-task-list-item (used by remark-gfm) always emits:
//   <input type="checkbox" disabled>
// Disabled inputs do NOT receive pointer events in the browser — clicks don't
// reach them, so they don't bubble to any ancestor's onClick handler.
// This tiny plugin strips the `disabled` property from every task-list checkbox
// in the hast so the inputs become clickable. We still call e.preventDefault()
// in the wrapper's onClick so the browser never toggles the checkbox itself —
// the markdown string remains the single source of truth.
const rehypeEnableTaskCheckboxes: Plugin<[], Root> = () => (tree: Root) => {
  visit(tree, 'element', (node: Element) => {
    if (
      node.tagName === 'input' &&
      node.properties?.type === 'checkbox' &&
      node.properties?.disabled === true
    ) {
      // Remove disabled so the input receives pointer events and clicks bubble
      delete node.properties.disabled;
      // Mark it as read-only at the HTML level — actual toggling is done via
      // the wrapper onClick + onToggleTask callback, not the input itself.
      node.properties.readOnly = true;
      // Add a CSS class so we can style the cursor
      const existing = node.properties.className;
      node.properties.className = [
        ...(Array.isArray(existing) ? existing : existing ? [existing] : []),
        'task-checkbox',
      ];
    }
  });
};

// ─── Types ───────────────────────────────────────────────────────────────────

interface MarkdownRendererProps {
  content: string;
  resolveWikilink?: (noteName: string) => string | null;
  onWikilinkClick?: (href: string) => void;
  onToggleTask?: (taskIndex: number) => void;
  className?: string;
}

// rehype-react requires these from the JSX runtime
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const production = { Fragment: (prod as any).Fragment, jsx: (prod as any).jsx, jsxs: (prod as any).jsxs };

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Build the unified processor. Recreated only when resolveWikilink changes. */
function buildProcessor(resolveWikilink?: (name: string) => string | null) {
  return unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkCallouts)
    .use(wikiLinkPlugin, {
      // Transform [[Note Title]] → resolved URL, or '#' when not found
      pageResolver: (name: string) => [name],
      hrefTemplate: (name: string) => resolveWikilink?.(name) ?? '#',
      // Support [[note|alias]] syntax
      aliasDivider: '|',
      // CSS class on the generated anchor; 'new' appended by remark-wiki-link when not in permalinks
      wikiLinkClassName: 'wikilink',
      newClassName: 'wikilink-new',
    })
    .use(remarkRehype, { allowDangerousHtml: false })
    .use(rehypeFoldableHeadings)
    .use(rehypeHighlight, { detect: true, ignoreMissing: true })
    .use(rehypeEnableTaskCheckboxes)
    .use(rehypeReact, {
      ...production,
      components: buildComponents(),
    });
}

/** Build the rehype-react component overrides. */
function buildComponents() {
  return {
    // Wikilinks come through as <a class="wikilink"> (or "wikilink wikilink-new")
    // from remark-wiki-link. We add data-wikilink and style resolved vs. unresolved
    // links differently: href="#" means the resolver returned null → apply wikilink-new.
    a: ({
      href,
      className,
      children,
      ...rest
    }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { children?: React.ReactNode }) => {
      const isWikilink =
        typeof className === 'string' && className.includes('wikilink');
      if (isWikilink) {
        // href === '#' means resolveWikilink returned null (document not found)
        const isNew = href === '#';

        // Extract the original note name from the child text
        const noteName =
          typeof children === 'string'
            ? children
            : Array.isArray(children)
            ? (children as React.ReactNode[])
                .map((c) => (typeof c === 'string' ? c : ''))
                .join('')
            : String(children ?? '');

        return (
          <a
            href={href}
            className={cn(
              'wikilink underline-offset-2 hover:underline',
              isNew
                ? 'wikilink-new text-muted-foreground opacity-70'
                : 'text-primary',
            )}
            data-wikilink={noteName}
            {...rest}
          >
            {children}
          </a>
        );
      }
      return (
        <a
          href={href}
          className={cn('text-blue-600 dark:text-blue-400 hover:underline underline-offset-2', className)}
          target={href?.startsWith('http') ? '_blank' : undefined}
          rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
          {...rest}
        >
          {children}
        </a>
      );
    },

    // Code blocks
    pre: ({ children, ...props }: React.HTMLAttributes<HTMLPreElement>) => (
      <pre
        className={cn(
          'my-4 overflow-x-auto rounded-lg bg-zinc-950 dark:bg-zinc-900 p-4 text-sm',
          props.className
        )}
        {...props}
      >
        {children}
      </pre>
    ),

    code: ({ children, className, ...props }: React.HTMLAttributes<HTMLElement>) => {
      // Inline code (no language class)
      const isBlock = typeof className === 'string' && className.startsWith('language-');
      if (!isBlock) {
        return (
          <code
            className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.85em] text-foreground"
            {...props}
          >
            {children}
          </code>
        );
      }
      return (
        <code className={cn('block font-mono text-sm text-zinc-100', className)} {...props}>
          {children}
        </code>
      );
    },

    // Blockquotes — callouts arrive here as <div data-callout="..."> because
    // remarkCallouts rewrites their hName. Plain blockquotes still use <blockquote>.
    blockquote: ({ children, ...props }: React.HTMLAttributes<HTMLQuoteElement>) => (
      <blockquote
        className="my-4 border-l-4 border-border pl-4 italic text-muted-foreground"
        {...props}
      >
        {children}
      </blockquote>
    ),

    // Tables
    table: ({ children, ...props }: React.TableHTMLAttributes<HTMLTableElement>) => (
      <div className="my-4 overflow-x-auto">
        <table
          className="w-full border-collapse text-sm"
          {...props}
        >
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => (
      <thead className="border-b border-border bg-muted/50" {...props}>
        {children}
      </thead>
    ),
    th: ({ children, ...props }: React.ThHTMLAttributes<HTMLTableCellElement>) => (
      <th
        className="px-4 py-2 text-left font-semibold text-foreground"
        {...props}
      >
        {children}
      </th>
    ),
    td: ({ children, ...props }: React.TdHTMLAttributes<HTMLTableCellElement>) => (
      <td
        className="border-b border-border px-4 py-2 text-foreground"
        {...props}
      >
        {children}
      </td>
    ),

    // Headings
    h1: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h1 className="mt-8 mb-4 text-3xl font-bold tracking-tight" {...props}>{children}</h1>
    ),
    h2: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h2 className="mt-6 mb-3 text-2xl font-semibold" {...props}>{children}</h2>
    ),
    h3: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h3 className="mt-5 mb-2 text-xl font-semibold" {...props}>{children}</h3>
    ),
    h4: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h4 className="mt-4 mb-2 text-lg font-medium" {...props}>{children}</h4>
    ),
    h5: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h5 className="mt-3 mb-1 text-base font-medium" {...props}>{children}</h5>
    ),
    h6: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h6 className="mt-3 mb-1 text-sm font-medium text-muted-foreground" {...props}>{children}</h6>
    ),

    // Paragraphs
    p: ({ children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
      <p className="my-3 leading-relaxed" {...props}>{children}</p>
    ),

    // Lists
    ul: ({ children, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
      <ul className="my-3 list-disc pl-6 space-y-1" {...props}>{children}</ul>
    ),
    ol: ({ children, ...props }: React.OlHTMLAttributes<HTMLOListElement>) => (
      <ol className="my-3 list-decimal pl-6 space-y-1" {...props}>{children}</ol>
    ),

    // Horizontal rule
    hr: (props: React.HTMLAttributes<HTMLHRElement>) => (
      <hr className="my-6 border-border" {...props} />
    ),

    // Strong / em / del
    strong: ({ children, ...props }: React.HTMLAttributes<HTMLElement>) => (
      <strong className="font-semibold" {...props}>{children}</strong>
    ),
    em: ({ children, ...props }: React.HTMLAttributes<HTMLElement>) => (
      <em className="italic" {...props}>{children}</em>
    ),
    del: ({ children, ...props }: React.HTMLAttributes<HTMLElement>) => (
      <del className="line-through text-muted-foreground" {...props}>{children}</del>
    ),
  };
}

// ─── Component ───────────────────────────────────────────────────────────────

export function MarkdownRenderer({
  content,
  resolveWikilink,
  onWikilinkClick,
  onToggleTask,
  className,
}: MarkdownRendererProps) {
  const [rendered, setRendered] = useState<React.ReactElement>(createElement(Fragment));

  // ── Optimistic task overrides ──────────────────────────────────────────────
  // When the user clicks a checkbox we know instantly what the new checked state
  // should be, but the async unified pipeline takes ~1-2 s to re-render.
  // We store a Map<checkboxIndex, checkedBoolean> of pending overrides and apply
  // them synchronously inside the `li` component override below — no DOM mutation,
  // just a React state update that happens in the same frame as the click.
  // When the pipeline finishes and sets new `rendered` output, we clear all
  // overrides because the new tree already reflects the correct markdown state.
  const [taskOverrides, setTaskOverrides] = useState<Map<number, boolean>>(new Map());
  const taskOverridesRef = useRef(taskOverrides);
  useEffect(() => { taskOverridesRef.current = taskOverrides; }, [taskOverrides]);

  // Stable refs for callbacks — updated on every render so handlers always
  // call the latest version without re-registering event listeners.
  const onToggleTaskRef = useRef(onToggleTask);
  useEffect(() => {
    onToggleTaskRef.current = onToggleTask;
  }, [onToggleTask]);

  const onWikilinkClickRef = useRef(onWikilinkClick);
  useEffect(() => {
    onWikilinkClickRef.current = onWikilinkClick;
  }, [onWikilinkClick]);

  // Rebuild processor only when resolveWikilink identity changes.
  // resolveWikilink should be stable (useCallback) at the call site.
  const processor = useMemo(
    () => buildProcessor(resolveWikilink),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolveWikilink]
  );

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        const file = await processor.process(content);
        if (!cancelled) {
          // Pipeline finished — clear optimistic overrides, the new tree is correct
          setTaskOverrides(new Map());
          setRendered(file.result as React.ReactElement);
        }
      } catch (err) {
        console.error('[MarkdownRenderer] Processing error:', err);
        if (!cancelled) {
          setTaskOverrides(new Map());
          setRendered(
            createElement('pre', { className: 'text-sm whitespace-pre-wrap' }, content)
          );
        }
      }
    }

    run();

    return () => {
      cancelled = true;
    };
  }, [content, processor]);

  // ── Wrapper click handler ──────────────────────────────────────────────────
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleWrapperClick = useCallback(
    (e: React.MouseEvent) => {
      const checkbox = (e.target as HTMLElement).closest('input[type="checkbox"]');
      if (!checkbox) return;

      // Block the browser's own toggle — we drive state from markdown
      e.preventDefault();

      const allCheckboxes = wrapperRef.current?.querySelectorAll<HTMLInputElement>(
        'input[type="checkbox"]'
      );
      if (!allCheckboxes) return;

      const index = Array.from(allCheckboxes).indexOf(checkbox as HTMLInputElement);
      if (index === -1) return;

      // Determine what the new state should be.
      // Read from the override map first (handles rapid successive clicks correctly),
      // then fall back to the current DOM checked state.
      const currentChecked =
        taskOverridesRef.current.has(index)
          ? taskOverridesRef.current.get(index)!
          : (checkbox as HTMLInputElement).checked;
      const nowChecked = !currentChecked;

      // Synchronous React state update — both checkbox and strikethrough render
      // in the same frame via the li override in the rendered tree (see below).
      setTaskOverrides((prev) => {
        const next = new Map(prev);
        next.set(index, nowChecked);
        return next;
      });

      onToggleTaskRef.current?.(index);
    },
    []
  );

  // ── Wikilink click handler ─────────────────────────────────────────────────
  // Uses native DOM delegation (not React) so it doesn't interfere with the
  // React onClick above.
  const handleWikilinkClick = useCallback((e: MouseEvent) => {
    const anchor = (e.target as HTMLElement).closest('a.wikilink');
    if (!anchor) return;
    e.preventDefault();
    const href = anchor.getAttribute('href');
    if (href && href !== '#') {
      onWikilinkClickRef.current?.(href);
    }
  }, []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    el.addEventListener('click', handleWikilinkClick);
    return () => el.removeEventListener('click', handleWikilinkClick);
  }, [handleWikilinkClick]);

  // ── Apply task overrides to the rendered tree ──────────────────────────────
  // Walk the React element tree and patch li.task-list-item nodes whose checkbox
  // index appears in taskOverrides, overriding both the input's checked prop and
  // the text's strikethrough style — all within React's own render cycle.
  const patchedRendered = useMemo(() => {
    if (taskOverrides.size === 0) return rendered;
    let checkboxIndex = 0;
    return patchTaskItems(rendered, taskOverrides, { index: checkboxIndex });
  }, [rendered, taskOverrides]);

  return (
    <div
      ref={wrapperRef}
      onClick={handleWrapperClick}
      className={cn(
        'markdown-body text-foreground text-[15px] leading-relaxed',
        className
      )}
    >
      {patchedRendered}
    </div>
  );
}

// ─── patchTaskItems ───────────────────────────────────────────────────────────
// Recursively walk a React element tree. For every li.task-list-item found,
// check if it contains a checkbox whose global index is in the overrides map.
// If so, clone the li with the overridden checked state and matching strikethrough.
// Uses a shared mutable counter object so the index increments correctly across
// the entire tree regardless of nesting depth.

function patchTaskItems(
  node: React.ReactNode,
  overrides: Map<number, boolean>,
  counter: { index: number },
): React.ReactNode {
  if (!React.isValidElement(node)) {
    return node;
  }

  const el = node as React.ReactElement<React.HTMLAttributes<HTMLElement> & Record<string, unknown>>;
  const props = el.props as Record<string, unknown>;
  const children = props.children as React.ReactNode | undefined;

  // Check if this is a task-list li
  const isTaskLi =
    el.type === 'li' &&
    typeof props.className === 'string' &&
    props.className.includes('task-list-item');

  if (isTaskLi) {
    // Find the checkbox inside this li to get its index
    const cbIndex = counter.index;
    counter.index += 1;

    if (overrides.has(cbIndex)) {
      const nowChecked = overrides.get(cbIndex)!;
      // Clone the li's children, patching the input and text nodes
      const patchedChildren = patchTaskLiChildren(children, nowChecked);
      return React.cloneElement(el, {}, ...React.Children.toArray(patchedChildren));
    }
    return node;
  }

  // Not a task li — recurse into children
  if (!children) return node;

  const childArray = React.Children.toArray(children);
  const patchedChildren = childArray.map((child) =>
    patchTaskItems(child, overrides, counter)
  );

  // Only clone if something actually changed
  const changed = patchedChildren.some((c, i) => c !== childArray[i]);
  if (!changed) return node;

  return React.cloneElement(el, {}, ...patchedChildren);
}

// Patch the immediate children of a li.task-list-item:
// - input[type=checkbox]: override `checked`
// - everything else: apply or remove line-through + muted color
function patchTaskLiChildren(
  children: React.ReactNode,
  checked: boolean,
): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;

    const childEl = child as React.ReactElement<React.HTMLAttributes<HTMLElement> & Record<string, unknown>>;
    const childProps = childEl.props as Record<string, unknown>;

    // The checkbox input
    if (childEl.type === 'input' && childProps.type === 'checkbox') {
      return React.cloneElement(childEl, { checked, onChange: () => {} } as Record<string, unknown>);
    }

    // The paragraph wrapping the text — recurse into it
    if (childEl.type === 'p') {
      const patchedPChildren = React.Children.map(childProps.children as React.ReactNode, (pChild) => {
        if (!React.isValidElement(pChild)) {
          // Plain text node — wrap in a span with the appropriate style
          if (typeof pChild === 'string' && pChild.trim()) {
            return (
              <span
                style={{
                  textDecoration: checked ? 'line-through' : 'none',
                  opacity: checked ? 0.6 : 1,
                }}
              >
                {pChild}
              </span>
            );
          }
          return pChild;
        }
        const pChildEl = pChild as React.ReactElement<React.HTMLAttributes<HTMLElement> & Record<string, unknown>>;
        // Skip the checkbox input inside the p
        if (pChildEl.type === 'input') return pChild;
        // Everything else (del, strong, em, span, text wrappers) — apply style
        return React.cloneElement(pChildEl, {
          style: {
            ...(typeof pChildEl.props.style === 'object' ? pChildEl.props.style : {}),
            textDecoration: checked ? 'line-through' : 'none',
            opacity: checked ? 0.6 : 1,
          },
        } as Record<string, unknown>);
      });
      return React.cloneElement(childEl, {}, ...React.Children.toArray(patchedPChildren ?? []));
    }

    return child;
  });
}
